import { EyeOutlined } from '@ant-design/icons';
import { Col, Form, getDefaultSortOrder, Icons, Input, List, Row, Select, Space, Table, TextField, useTable } from '@pankod/refine-antd';
import { CrudFilters, IResourceComponentsProps } from '@pankod/refine-core';
import routerProvider from '@pankod/refine-react-router-v6';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import type { ColumnsType } from 'antd/es/table';
import { ButtonColumn } from 'components/buttonColumn';
import { ButtonExport } from 'components/buttonExport';
import { ButtonFilter } from 'components/buttonFilter';
import React, { useState } from 'react';
import { ITransaction, ITransactionResponse } from 'types/transaction.interface';
import { formatDateTime, formatNumberWith2DecimalPlaces, getOrderNumber, includesClassName } from 'utils';
import { useSearchParams } from 'react-router-dom';
import useMediaScreen from 'hooks/useMediaScreen';

export const TransactionSubscriptionsList: React.FC<IResourceComponentsProps> = () => {
    const [searchParams] = useSearchParams();
    const userParam = searchParams.get('user');
    const [checkFilter, setCheckFilter] = useState<boolean>(false);
    const { isMobile } = useMediaScreen();

    const { tableProps, sorter, searchFormProps }: any = useTable<ITransactionResponse>({
        resource: `subscription-transactions${userParam && !checkFilter ? `?search=${encodeURIComponent(userParam)}` : ``}`,
        initialPageSize: 100,
        onSearch: (params) => {
            const filters: CrudFilters = [];
            const input: any = params;
            filters.push({
                field: 'search',
                operator: 'in',
                value: {
                    search: checkFilter ? input.search || '' : decodeURIComponent(userParam || ''),
                    status: input.status || '',
                },
            });
            return filters;
        },
    });

    const { Link } = routerProvider;
    const current = tableProps?.pagination?.current;
    const pageSize = tableProps?.pagination?.pageSize;

    const defaultColumn: CheckboxValueType[] = [
        'user',
        'paypalEmail',
        'packageSubscription',
        'days',
        'dateEnd',
        'dateStart',
        'USDtoContributorSubscriptionPool',
        'USDtoCompany',
        'netIncome',
        'payPalFees',
        'cost',
        'isCancel',
        'paypalSubscriptionId',
        'status',
        'DL',
    ];

    const [hideCols, setHideCols] = useState<CheckboxValueType[]>(defaultColumn);

    const options = [
        { label: 'User', value: 'user' },
        // { label: 'Paypal email', value: 'paypalEmail' },
        { label: 'Subscription', value: 'packageSubscription' },
        { label: 'Days', value: 'days' },
        { label: 'Date Start', value: 'dateStart' },
        { label: 'Date End', value: 'dateEnd' },
        { label: 'Cost', value: 'cost' },
        { label: 'Paypal Fees', value: 'payPalFees' },
        { label: 'Net Income', value: 'netIncome' },
        { label: 'To Company', value: 'USDtoCompany' },
        {
            label: 'To Contributor Subscription Pool',
            value: 'USDtoContributorSubscriptionPool',
        },
        { label: 'Subscription ID', value: 'paypalSubscriptionId' },
        { label: 'Status', value: 'status' },
        { label: 'DL’S', value: 'DL' },
    ];

    const optionsStatus = [
        { label: 'Active', value: 'false' },
        { label: 'Canceled', value: 'true' },
    ];

    const columns: ColumnsType<ITransaction> = [
        {
            title: 'Index',
            dataIndex: '_id',
            key: '_id',
            render: (value, item, index) => <TextField value={getOrderNumber(current, pageSize, index)} />,
        },
        {
            title: 'User',
            dataIndex: 'user',
            key: 'user',
            render: (_, record: any) => (
                <Link style={{ color: 'unset' }} target='_blank' to={`/users/edit/${record?.user?._id}`}>
                    {record?.user?.email}
                </Link>
            ),

            className: includesClassName(hideCols, 'user'),
            defaultSortOrder: getDefaultSortOrder('user', sorter),
            sorter,
        },
        // {
        //     title: 'Paypal email',
        //     dataIndex: 'paypalEmail',
        //     key: 'paypalEmail',
        //     render: (_, record: any) => (
        //         <Link style={{ color: 'unset' }} target='_blank' to={`/users/edit/${record?.user?._id}`}>
        //             {record?.user?.paypalEmail}
        //         </Link>
        //     ),

        //     className: includesClassName(hideCols, 'paypalEmail'),
        //     defaultSortOrder: getDefaultSortOrder('paypalEmail', sorter),
        //     sorter,
        // },
        {
            title: 'Subscription',
            dataIndex: 'packageSubscription',
            key: 'packageSubscription',
            render: (_, record: any) => (
                <Link style={{ color: 'unset' }} target='_blank' to={`/token-packages/edit/${record?.packageSubscription?._id}`}>
                    {record?.packageSubscription?.name}
                </Link>
            ),
            className: includesClassName(hideCols, 'packageSubscription'),
            defaultSortOrder: getDefaultSortOrder('packageSubscription', sorter),
            sorter,
        },
        {
            title: 'Days',
            dataIndex: 'days',
            key: 'days',
            render: (_, record: any) => <TextField value={record?.days} />,
            className: includesClassName(hideCols, 'days'),
            defaultSortOrder: getDefaultSortOrder('days', sorter),
            sorter,
        },
        {
            title: 'Cost($)',
            dataIndex: 'cost',
            key: 'cost',
            render: (_, record: any) => <TextField value={formatNumberWith2DecimalPlaces(record?.cost)} />,
            className: includesClassName(hideCols, 'cost'),
            defaultSortOrder: getDefaultSortOrder('cost', sorter),
            sorter,
        },
        {
            title: 'Paypal Fees($)',
            dataIndex: 'payPalFees',
            key: 'payPalFees',
            render: (_, record: any) => <TextField value={formatNumberWith2DecimalPlaces(record?.payPalFees)} />,
            className: includesClassName(hideCols, 'payPalFees'),
            defaultSortOrder: getDefaultSortOrder('payPalFees', sorter),
            sorter,
        },
        {
            title: 'Net Income($)',
            dataIndex: 'netIncome',
            key: 'netIncome',
            render: (_, record: any) => <TextField value={formatNumberWith2DecimalPlaces(record?.netIncome)} />,
            className: includesClassName(hideCols, 'netIncome'),
            defaultSortOrder: getDefaultSortOrder('netIncome', sorter),
            sorter,
        },
        {
            title: 'To Company($)',
            dataIndex: 'USDtoCompany',
            key: 'USDtoCompany',
            render: (_, record: any) => <TextField value={formatNumberWith2DecimalPlaces(record?.USDtoCompany)} />,
            className: includesClassName(hideCols, 'USDtoCompany'),
            defaultSortOrder: getDefaultSortOrder('USDtoCompany', sorter),
            sorter,
        },
        {
            title: 'To Contributor Subscription Pool($)',
            dataIndex: 'USDtoContributorSubscriptionPool',
            key: 'USDtoContributorSubscriptionPool',
            render: (_, record: any) => <TextField value={formatNumberWith2DecimalPlaces(record?.USDtoContributorSubscriptionPool)} />,
            className: includesClassName(hideCols, 'USDtoContributorSubscriptionPool'),
            defaultSortOrder: getDefaultSortOrder('USDtoContributorSubscriptionPool', sorter),
            sorter,
        },
        {
            title: 'Paypal Payee Transaction ID',
            dataIndex: 'paypalPayeeTransactionId',
            key: 'paypalPayeeTransactionId',
            render: (_, record: any) => <TextField value={record?.paypalPayeeTransactionId} />,
            className: includesClassName(hideCols, 'paypalSubscriptionId'),
            defaultSortOrder: getDefaultSortOrder('paypalSubscriptionId', sorter),
            sorter,
        },
        {
            title: 'Paypal Subscription ID',
            dataIndex: 'paypalSubscriptionId',
            key: 'paypalSubscriptionId',
            render: (_, record: any) => <TextField value={record?.paypalSubscriptionId} />,
            className: includesClassName(hideCols, 'paypalSubscriptionId'),
            defaultSortOrder: getDefaultSortOrder('paypalSubscriptionId', sorter),
            sorter,
        },
        {
            title: 'Date Start',
            dataIndex: 'dateStart',
            key: 'dateStart',
            render: (value) => <TextField value={formatDateTime(value)} />,
            className: includesClassName(hideCols, 'dateStart'),
            defaultSortOrder: getDefaultSortOrder('dateStart', sorter),
            sorter,
        },
        {
            title: 'Date End',
            dataIndex: 'dateEnd',
            key: 'dateEnd',
            render: (value) => <TextField value={formatDateTime(value)} />,
            className: includesClassName(hideCols, 'dateEnd'),
            defaultSortOrder: getDefaultSortOrder('dateEnd', sorter),
            sorter,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (_, record: any) => <TextField value={record?.isCancel ? 'Canceled' : 'Active'} />,
            className: includesClassName(hideCols, 'status'),
            defaultSortOrder: getDefaultSortOrder('status', sorter),
        },
        {
            title: 'DL’S',
            dataIndex: '',
            key: 'DL',
            render: (_, record: any) => (
                <Link to={`/subscription-package-purchases/show/${record._id}`}>
                    <TextField value={record?.maxDownloadsPerMonth - record.remainingDownloadsPerMonth} />
                </Link>
            ),

            className: includesClassName(hideCols, 'DL'),
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_, record: any) => (
                <Space>
                    <a href={`https://www.paypal.com/billing/subscriptions/${record?.paypalSubscriptionId}`} style={{ color: 'unset' }} target='_blank' rel='noreferrer'>
                        <EyeOutlined style={{ fontSize: '18px', marginTop: '4px', cursor: 'pointer' }} />
                    </a>
                    {/* <DeleteButton hideText size='small' recordItemId={record._id} /> */}
                </Space>
            ),
        },
    ];

    return (
        <List>
            <Row style={{ display: 'block' }}>
                <Col>
                    <Form layout='vertical' {...searchFormProps}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginBottom: '20px',
                                flexDirection: isMobile ? 'column' : 'row',
                            }}
                        >
                            <Form.Item label='Search' name='search' style={{ width: isMobile ? '100%' : '50%' }} initialValue={decodeURIComponent(userParam || '')}>
                                <Input placeholder='User email, paypal payee transaction id, subscription' prefix={<Icons.SearchOutlined />} />
                            </Form.Item>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Form.Item
                                    label='Status'
                                    style={{
                                        width: isMobile ? '100%' : '30%',
                                        marginLeft: isMobile ? '0' : '15px',
                                    }}
                                    name='status'
                                >
                                    <Select
                                        style={{ width: isMobile ? '100%' : '200px' }}
                                        defaultValue={{ value: '', label: 'All' }}
                                        filterOption={(input, option: any) => option.label.toLowerCase().includes(input.toLowerCase())}
                                        options={[{ value: '', label: 'All' }, ...optionsStatus]}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Form.Item>
                                <div onClick={() => setCheckFilter(true)}>
                                    <ButtonFilter />
                                </div>
                            </Form.Item>
                            <div>
                                <ButtonColumn options={options} setHideCols={setHideCols} defaultColumn={defaultColumn} />
                                <ButtonExport path='subscription-transactions' />
                            </div>
                        </div>
                    </Form>
                </Col>
                <Col>
                    <Table
                        {...tableProps}
                        pagination={{
                            ...tableProps?.pagination,
                            showSizeChanger: true,
                        }}
                        columns={columns}
                        rowKey='id'
                    />
                </Col>
            </Row>
        </List>
    );
};
